import React from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import type { MapConfig } from './index';
import type { ResolvedBaseMap } from './useBaseMap';

/**
 * Applies dynamic map constraints that cannot be set via MapContainer props
 * (interactions, maxBounds). Supports per-basemap maxBounds with fallback:
 * active basemap → default basemap (first) → global config → no bounds.
 */
const MapConfigurator: React.FC<{
  mapConfig: MapConfig;
  activeBaseMapId?: string;
  resolvedBaseMaps?: ResolvedBaseMap[];
}> = ({ mapConfig, activeBaseMapId, resolvedBaseMaps }) => {
  const map = useMap();

  React.useEffect(() => {
    const { interactionsActivees } = mapConfig;

    // Interactions: zoom
    if (!interactionsActivees.zoom) {
      map.scrollWheelZoom.disable();
      map.doubleClickZoom.disable();
      map.touchZoom.disable();
      map.boxZoom.disable();
    } else {
      map.scrollWheelZoom.enable();
      map.doubleClickZoom.enable();
      map.touchZoom.enable();
      map.boxZoom.enable();
    }

    // Interactions: pan
    if (!interactionsActivees.pan) {
      map.dragging.disable();
    } else {
      map.dragging.enable();
    }

    // maxBounds: per-basemap → default basemap fallback → global config
    const activeMap = resolvedBaseMaps?.find(rbm => rbm.id === activeBaseMapId);
    const defaultMap = resolvedBaseMaps?.[0];
    const effectiveBounds = activeMap?.maxBounds ?? defaultMap?.maxBounds ?? mapConfig.maxBounds;

    if (effectiveBounds) {
      map.setMaxBounds(L.latLngBounds(
        L.latLng(effectiveBounds.southWest.lat, effectiveBounds.southWest.lng),
        L.latLng(effectiveBounds.northEast.lat, effectiveBounds.northEast.lng)
      ));
    } else {
      map.setMaxBounds(L.latLngBounds(L.latLng(-90, -180), L.latLng(90, 180)));
    }
  }, [map, mapConfig, activeBaseMapId, resolvedBaseMaps]);

  return null;
};

export default MapConfigurator;
